import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import {
  Mail, MessageSquare, Clock, Phone, Voicemail, ListTodo, GitBranch, ArrowRightLeft, Flag, Zap,
  Plus, Trash2, Copy, ChevronUp, ChevronDown, Settings2, GitFork, X, CheckCircle2
} from 'lucide-react';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import BBALoader from '../../components/BBALoader';
import PortalBackground from '../../components/PortalBackground';

/**
 * Drip Campaigns (CRM ▸ Templates ▸ General). A visual, Zapier-style outline
 * of how we nurture leads at each pipeline stage. Reference only — nothing
 * here sends messages or feeds other pages.
 *
 * One Firestore doc per version in `dripCampaignVersions/{id}`:
 *   { name, status: 'active'|'draft'|'archived', notes,
 *     stages: { [stageKey]: { workflows: [{ id, name, trigger, steps: [] }] } },
 *     createdAt, createdBy, updatedAt, updatedBy, basedOn }
 */

const COLLECTION = 'dripCampaignVersions';

// Mirrors the Leads pipeline stages (Accounts.js) plus Lost for re-engagement.
const STAGES = [
  { key: 'prospects', title: 'Prospects', color: '#EF4444' },
  { key: 'lead-received', title: 'Lead Received', color: '#3B82F6' },
  { key: 'discovery-call', title: 'Discovery Call', color: '#8B5CF6' },
  { key: 'assessment', title: 'Assessment', color: '#10B981' },
  { key: 'onboarding-call', title: 'Onboarding Call', color: '#F59E0B' },
  { key: 'proposal', title: 'Proposal', color: '#EC4899' },
  { key: 'lost', title: 'Lost', color: '#6B7280' }
];

const STEP_TYPES = {
  email: { label: 'Email', icon: Mail, soft: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
  text: { label: 'Text', icon: MessageSquare, soft: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
  delay: { label: 'Delay', icon: Clock, soft: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
  call: { label: 'Call', icon: Phone, soft: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
  voicemail: { label: 'Voicemail', icon: Voicemail, soft: 'bg-indigo-50 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400' },
  task: { label: 'Task', icon: ListTodo, soft: 'bg-gray-100 dark:bg-gray-700/60', text: 'text-gray-600 dark:text-gray-300' },
  condition: { label: 'Condition', icon: GitBranch, soft: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' },
  stage: { label: 'Move Stage', icon: ArrowRightLeft, soft: 'bg-pink-50 dark:bg-pink-900/30', text: 'text-pink-600 dark:text-pink-400' },
  end: { label: 'End', icon: Flag, soft: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400' }
};

const STATUS_STYLES = {
  active: { label: 'Active', className: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200' },
  archived: { label: 'Archived', className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' }
};

const MERGE_FIELDS = ['{{first_name}}', '{{company_name}}', '{{rep_name}}', '{{booking_link}}'];

const UNIT_MINUTES = { minutes: 1, hours: 60, days: 1440, weeks: 10080 };

const uid = () => Math.random().toString(36).slice(2, 10);

const defaultWorkflow = (stage, name = 'Main sequence') => ({
  id: uid(),
  name,
  trigger: `Lead enters ${stage.title}`,
  steps: []
});

const newStep = (type) => {
  switch (type) {
    case 'email': return { id: uid(), type, subject: '', body: '', from: '' };
    case 'text': return { id: uid(), type, body: '', from: '' };
    case 'delay': return { id: uid(), type, amount: 1, unit: 'days', note: '' };
    case 'call': return { id: uid(), type, title: '', body: '', from: '' };
    case 'voicemail': return { id: uid(), type, body: '' };
    case 'task': return { id: uid(), type, title: '', assignee: '', body: '' };
    case 'condition': return { id: uid(), type, condition: '', ifYes: 'Continue sequence', ifNo: 'Exit sequence' };
    case 'stage': return { id: uid(), type, targetStage: '' };
    case 'end': return { id: uid(), type, body: '' };
    default: return { id: uid(), type };
  }
};

// Guarantees every stage has a workflows array so older/partial docs render.
const normalizeStages = (stages = {}) => {
  const out = {};
  STAGES.forEach(stage => {
    const workflows = stages[stage.key]?.workflows;
    out[stage.key] = { workflows: Array.isArray(workflows) ? workflows : [defaultWorkflow(stage)] };
  });
  return out;
};

const delayMinutes = (step) => Math.max(0, Number(step.amount) || 0) * (UNIT_MINUTES[step.unit] || 1440);

const delayLabel = (step) => {
  const amount = Number(step.amount) || 0;
  const unit = amount === 1 ? (step.unit || 'days').replace(/s$/, '') : (step.unit || 'days');
  return `Wait ${amount} ${unit}`;
};

const formatTotal = (minutes) => {
  if (!minutes) return 'Same day';
  if (minutes >= 1440) {
    const days = +(minutes / 1440).toFixed(1);
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (minutes >= 60) {
    const hours = +(minutes / 60).toFixed(1);
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} min`;
};

const dayLabel = (offset) => {
  const day = Math.floor(offset / 1440) + 1;
  const hours = Math.floor((offset % 1440) / 60);
  return hours > 0 ? `Day ${day} · +${hours}h` : `Day ${day}`;
};

const stepSummary = (step) => {
  switch (step.type) {
    case 'email': return step.subject || 'No subject yet';
    case 'text': return step.body || 'No message yet';
    case 'call': return step.title || 'Call the lead';
    case 'voicemail': return step.body || 'Voicemail drop';
    case 'task': return step.title || 'Untitled task';
    case 'condition': return step.condition ? `If ${step.condition}` : 'Set a condition';
    case 'stage': return step.targetStage ? `Move to ${step.targetStage}` : 'Pick a stage';
    case 'end': return step.body || 'Sequence ends';
    default: return '';
  }
};

const workflowStats = (workflow) => {
  const steps = workflow?.steps || [];
  const count = (type) => steps.filter(s => s.type === type).length;
  return {
    steps: steps.filter(s => s.type !== 'delay').length,
    emails: count('email'),
    texts: count('text'),
    calls: count('call') + count('voicemail'),
    duration: steps.reduce((sum, s) => sum + (s.type === 'delay' ? delayMinutes(s) : 0), 0)
  };
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const inputClass =
  'w-full text-sm px-3 py-2 rounded-lg border bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-500';
const labelClass = 'block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1';
const cardClass = 'bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm';

// "+" between nodes; opens a step-type picker in place.
const InsertButton = ({ open, onOpen, onClose, onPick }) => (
  <div className="relative flex flex-col items-center">
    <span className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
    <button
      type="button"
      onClick={open ? onClose : onOpen}
      className={`w-7 h-7 rounded-full border flex items-center justify-center transition ${
        open
          ? 'bg-red-600 border-red-600 text-white'
          : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600 text-gray-500 hover:border-red-500 hover:text-red-600'
      }`}
      aria-label="Add step"
    >
      <Plus className="w-4 h-4" />
    </button>
    <span className="w-px h-4 bg-gray-300 dark:bg-gray-600" />
    {open && (
      <>
        <div className="fixed inset-0 z-20" onClick={onClose} />
        <div className={`absolute top-12 left-1/2 -translate-x-1/2 z-30 w-72 p-2 ${cardClass} shadow-lg`}>
          <div className="grid grid-cols-3 gap-1.5">
            {Object.entries(STEP_TYPES).map(([type, meta]) => {
              const Icon = meta.icon;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => onPick(type)}
                  className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <span className={`w-8 h-8 rounded-lg ${meta.soft} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${meta.text}`} />
                  </span>
                  <span className="text-[11px] font-medium text-gray-700 dark:text-gray-300">{meta.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </>
    )}
  </div>
);

const NodeActions = ({ index, total, onMove, onDuplicate, onDelete, className = '' }) => (
  <div className={`flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity ${className}`} onClick={(e) => e.stopPropagation()}>
    <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30" aria-label="Move up">
      <ChevronUp className="w-3.5 h-3.5" />
    </button>
    <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30" aria-label="Move down">
      <ChevronDown className="w-3.5 h-3.5" />
    </button>
    <button type="button" onClick={() => onDuplicate(index)} className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Duplicate">
      <Copy className="w-3.5 h-3.5" />
    </button>
    <button type="button" onClick={() => onDelete(index)} className="p-1 rounded text-gray-400 hover:text-red-600" aria-label="Delete">
      <Trash2 className="w-3.5 h-3.5" />
    </button>
  </div>
);

const DripCampaigns = () => {
  const { currentUser } = useAuth();
  const [versions, setVersions] = useState([]); // [{ id, ...data }]
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null); // normalized stages of the selected version
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [stageKey, setStageKey] = useState(STAGES[0].key);
  const [workflowId, setWorkflowId] = useState(null);
  const [selectedNode, setSelectedNode] = useState('trigger'); // 'trigger' | step id
  const [insertAt, setInsertAt] = useState(null);

  const [versionModal, setVersionModal] = useState(null); // 'new' | 'settings'
  const [modalForm, setModalForm] = useState({});

  const userLabel = currentUser?.displayName || currentUser?.email || 'Unknown';

  const selectVersion = useCallback((version) => {
    setSelectedId(version?.id ?? null);
    setDraft(normalizeStages(version?.stages));
    setDirty(false);
    setSelectedNode('trigger');
    setWorkflowId(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, COLLECTION));
        const list = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
        if (cancelled) return;
        if (list.length === 0) {
          // First visit: an unsaved Version 1 that is created on first save.
          const starter = { id: null, name: 'Version 1', status: 'active', notes: '', stages: {} };
          setVersions([starter]);
          selectVersion(starter);
        } else {
          setVersions(list);
          selectVersion(list.find(v => v.status === 'active') || list[list.length - 1]);
        }
      } catch (err) {
        console.error('Load drip campaigns error:', err);
        if (!cancelled) setError('Failed to load drip campaigns. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectVersion]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const currentVersion = versions.find(v => v.id === selectedId) || null;
  const stage = STAGES.find(s => s.key === stageKey);
  const workflows = draft?.[stageKey]?.workflows || [];
  const workflow = workflows.find(w => w.id === workflowId) || workflows[0] || null;
  const steps = useMemo(() => workflow?.steps || [], [workflow]);
  const stats = useMemo(() => workflowStats(workflow), [workflow]);

  // Running day offset for each step (delays push later steps out).
  const offsets = useMemo(() => {
    let running = 0;
    return steps.map(step => {
      const at = running;
      if (step.type === 'delay') running += delayMinutes(step);
      return at;
    });
  }, [steps]);

  // Display numbers skip delays, which render as connectors rather than steps.
  const stepNumbers = useMemo(() => {
    let n = 0;
    return steps.map(step => (step.type === 'delay' ? null : ++n));
  }, [steps]);

  const selectedStep = steps.find(s => s.id === selectedNode) || null;

  // ---- draft mutations -------------------------------------------------

  const updateWorkflows = (updater) => {
    setDraft(prev => ({ ...prev, [stageKey]: { workflows: updater(prev[stageKey].workflows) } }));
    setDirty(true);
  };

  const updateWorkflow = (changes) => {
    if (!workflow) return;
    updateWorkflows(list => list.map(w => (w.id === workflow.id ? { ...w, ...changes } : w)));
  };

  const updateSteps = (updater) => {
    if (!workflow) return;
    updateWorkflows(list => list.map(w => (w.id === workflow.id ? { ...w, steps: updater(w.steps || []) } : w)));
  };

  const updateStep = (id, changes) => updateSteps(list => list.map(s => (s.id === id ? { ...s, ...changes } : s)));

  const insertStep = (index, type) => {
    const step = newStep(type);
    updateSteps(list => [...list.slice(0, index), step, ...list.slice(index)]);
    setSelectedNode(step.id);
    setInsertAt(null);
  };

  const moveStep = (index, dir) => updateSteps(list => {
    const next = [...list];
    const target = index + dir;
    if (target < 0 || target >= next.length) return list;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });

  const duplicateStep = (index) => {
    const copy = { ...steps[index], id: uid() };
    updateSteps(list => [...list.slice(0, index + 1), copy, ...list.slice(index + 1)]);
    setSelectedNode(copy.id);
  };

  const deleteStep = (index) => {
    const removed = steps[index];
    updateSteps(list => list.filter((_, i) => i !== index));
    if (removed?.id === selectedNode) setSelectedNode('trigger');
  };

  const addWorkflow = () => {
    const wf = defaultWorkflow(stage, `Sequence ${workflows.length + 1}`);
    updateWorkflows(list => [...list, wf]);
    setWorkflowId(wf.id);
    setSelectedNode('trigger');
  };

  const deleteWorkflow = () => {
    if (!workflow) return;
    if (!window.confirm(`Delete the "${workflow.name}" workflow from ${stage.title}?`)) return;
    updateWorkflows(list => list.filter(w => w.id !== workflow.id));
    setWorkflowId(null);
    setSelectedNode('trigger');
  };

  const switchStage = (key) => {
    setStageKey(key);
    setWorkflowId(null);
    setSelectedNode('trigger');
    setInsertAt(null);
  };

  // ---- persistence -----------------------------------------------------

  const handleSave = async () => {
    if (!currentVersion) return;
    setSaving(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      if (currentVersion.id) {
        await updateDoc(doc(db, COLLECTION, currentVersion.id), { stages: draft, updatedAt: now, updatedBy: userLabel });
        setVersions(prev => prev.map(v => (v.id === currentVersion.id ? { ...v, stages: draft, updatedAt: now, updatedBy: userLabel } : v)));
      } else {
        const data = {
          name: currentVersion.name,
          status: currentVersion.status,
          notes: currentVersion.notes || '',
          stages: draft,
          createdAt: now,
          createdBy: userLabel,
          updatedAt: now,
          updatedBy: userLabel
        };
        const ref = await addDoc(collection(db, COLLECTION), data);
        setVersions([{ id: ref.id, ...data }]);
        setSelectedId(ref.id);
      }
      setDirty(false);
    } catch (err) {
      console.error('Save drip campaign error:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSwitchVersion = (id) => {
    if (dirty && !window.confirm('Discard unsaved changes to this version?')) return;
    const version = versions.find(v => v.id === id);
    if (version) selectVersion(version);
  };

  const openNewVersion = () => {
    setModalForm({ name: `Version ${versions.length + 1}`, source: 'current', notes: '' });
    setVersionModal('new');
  };

  const openSettings = () => {
    setModalForm({ name: currentVersion?.name || '', notes: currentVersion?.notes || '', status: currentVersion?.status || 'draft' });
    setVersionModal('settings');
  };

  const createVersion = async () => {
    const name = (modalForm.name || '').trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      const data = {
        name,
        status: 'draft',
        notes: (modalForm.notes || '').trim(),
        stages: modalForm.source === 'blank' ? normalizeStages() : JSON.parse(JSON.stringify(draft)),
        basedOn: modalForm.source === 'blank' ? null : (currentVersion?.name || null),
        createdAt: now,
        createdBy: userLabel,
        updatedAt: now,
        updatedBy: userLabel
      };
      const ref = await addDoc(collection(db, COLLECTION), data);
      const created = { id: ref.id, ...data };
      // An unsaved starter version is dropped once a real one exists.
      setVersions(prev => [...prev.filter(v => v.id), created]);
      selectVersion(created);
      setVersionModal(null);
    } catch (err) {
      console.error('Create drip campaign version error:', err);
      setError('Failed to create version. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveSettings = async () => {
    const name = (modalForm.name || '').trim();
    if (!name || !currentVersion) return;
    const changes = { name, notes: (modalForm.notes || '').trim(), status: modalForm.status };
    setSaving(true);
    setError(null);
    try {
      if (currentVersion.id) {
        const now = new Date().toISOString();
        const batch = writeBatch(db);
        // Only one active version at a time; the previous one is archived.
        const demoted = changes.status === 'active'
          ? versions.filter(v => v.id && v.id !== currentVersion.id && v.status === 'active')
          : [];
        demoted.forEach(v => batch.update(doc(db, COLLECTION, v.id), { status: 'archived', updatedAt: now }));
        batch.update(doc(db, COLLECTION, currentVersion.id), { ...changes, updatedAt: now, updatedBy: userLabel });
        await batch.commit();
        setVersions(prev => prev.map(v => {
          if (v.id === currentVersion.id) return { ...v, ...changes, updatedAt: now, updatedBy: userLabel };
          if (demoted.some(d => d.id === v.id)) return { ...v, status: 'archived' };
          return v;
        }));
      } else {
        setVersions(prev => prev.map(v => (v === currentVersion ? { ...v, ...changes } : v)));
      }
      setVersionModal(null);
    } catch (err) {
      console.error('Update drip campaign version error:', err);
      setError('Failed to update version. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteVersion = async () => {
    if (!currentVersion?.id) return;
    if (versions.length <= 1) return;
    if (!window.confirm(`Delete "${currentVersion.name}"? This cannot be undone.`)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteDoc(doc(db, COLLECTION, currentVersion.id));
      const remaining = versions.filter(v => v.id !== currentVersion.id);
      setVersions(remaining);
      selectVersion(remaining.find(v => v.status === 'active') || remaining[remaining.length - 1]);
      setVersionModal(null);
    } catch (err) {
      console.error('Delete drip campaign version error:', err);
      setError('Failed to delete version. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ---- render helpers --------------------------------------------------

  const stageStepCount = (key) =>
    (draft?.[key]?.workflows || []).reduce((sum, w) => sum + (w.steps || []).filter(s => s.type !== 'delay').length, 0);

  const appendToken = (step, field, token) =>
    updateStep(step.id, { [field]: `${step[field] || ''}${step[field] && !/\s$/.test(step[field]) ? ' ' : ''}${token}` });

  const TokenChips = ({ step, field }) => (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {MERGE_FIELDS.map(token => (
        <button
          key={token}
          type="button"
          onClick={() => appendToken(step, field, token)}
          className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          {token}
        </button>
      ))}
    </div>
  );

  const renderNode = (step, index) => {
    const meta = STEP_TYPES[step.type] || STEP_TYPES.task;
    const Icon = meta.icon;
    const selected = selectedNode === step.id;
    const actionProps = { index, total: steps.length, onMove: moveStep, onDuplicate: duplicateStep, onDelete: deleteStep };

    if (step.type === 'delay') {
      return (
        <div className="group flex items-center gap-2">
          <span className="w-[100px]" />
          <button
            type="button"
            onClick={() => setSelectedNode(step.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition ${meta.soft} ${meta.text} ${
              selected ? 'border-red-500 ring-2 ring-red-500/30' : 'border-amber-200 dark:border-amber-800 hover:border-amber-400'
            }`}
          >
            <Icon className="w-4 h-4" />
            {delayLabel(step)}
          </button>
          <span className="w-[100px] flex justify-start"><NodeActions {...actionProps} /></span>
        </div>
      );
    }

    return (
      <div className="w-full max-w-md">
        <div
          role="button"
          tabIndex={0}
          onClick={() => setSelectedNode(step.id)}
          onKeyDown={(e) => { if (e.key === 'Enter') setSelectedNode(step.id); }}
          className={`group relative ${cardClass} p-3 cursor-pointer transition hover:shadow-md ${
            selected ? 'ring-2 ring-red-500 border-transparent' : ''
          }`}
        >
          <div className="flex items-start gap-3">
            <span className={`shrink-0 w-10 h-10 rounded-xl ${meta.soft} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${meta.text}`} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className={`text-[11px] font-semibold uppercase tracking-wide ${meta.text}`}>
                  {stepNumbers[index]}. {meta.label}
                </span>
                <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 whitespace-nowrap">
                  {dayLabel(offsets[index])}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{stepSummary(step)}</p>
              {step.type === 'email' && step.body && (
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{step.body}</p>
              )}
              {step.from && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">From {step.from}</p>
              )}
            </div>
          </div>
          {step.type === 'condition' && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="rounded-lg px-2.5 py-1.5 bg-green-50 dark:bg-green-900/20 text-xs">
                <span className="font-semibold text-green-700 dark:text-green-400">Yes → </span>
                <span className="text-gray-700 dark:text-gray-300">{step.ifYes || '—'}</span>
              </div>
              <div className="rounded-lg px-2.5 py-1.5 bg-red-50 dark:bg-red-900/20 text-xs">
                <span className="font-semibold text-red-700 dark:text-red-400">No → </span>
                <span className="text-gray-700 dark:text-gray-300">{step.ifNo || '—'}</span>
              </div>
            </div>
          )}
          <NodeActions
            {...actionProps}
            className="absolute -top-3 right-3 px-0.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm"
          />
        </div>
      </div>
    );
  };

  const renderEditor = () => {
    if (!workflow) return null;

    if (!selectedStep) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${stage.color}22` }}>
              <Zap className="w-4 h-4" style={{ color: stage.color }} />
            </span>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Workflow trigger</h3>
          </div>
          <div>
            <label className={labelClass}>Workflow name</label>
            <input className={inputClass} value={workflow.name} onChange={(e) => updateWorkflow({ name: e.target.value })} placeholder="e.g. No-show follow-up" />
          </div>
          <div>
            <label className={labelClass}>Trigger / who enters</label>
            <textarea className={inputClass} rows={3} value={workflow.trigger} onChange={(e) => updateWorkflow({ trigger: e.target.value })} placeholder={`Lead enters ${stage.title}`} />
          </div>
          <div>
            <label className={labelClass}>Goal / exit criteria</label>
            <textarea className={inputClass} rows={3} value={workflow.goal || ''} onChange={(e) => updateWorkflow({ goal: e.target.value })} placeholder="e.g. Lead books a discovery call — stop the sequence" />
          </div>
          <button type="button" onClick={deleteWorkflow} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
            Delete workflow
          </button>
        </div>
      );
    }

    const meta = STEP_TYPES[selectedStep.type] || STEP_TYPES.task;
    const Icon = meta.icon;
    const index = steps.findIndex(s => s.id === selectedStep.id);
    const set = (changes) => updateStep(selectedStep.id, changes);

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className={`w-8 h-8 rounded-lg ${meta.soft} flex items-center justify-center`}>
              <Icon className={`w-4 h-4 ${meta.text}`} />
            </span>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              {selectedStep.type === 'delay' ? 'Delay' : `Step ${stepNumbers[index]} · ${meta.label}`}
            </h3>
          </div>
          <button type="button" onClick={() => setSelectedNode('trigger')} className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {selectedStep.type === 'email' && (
          <>
            <div>
              <label className={labelClass}>From</label>
              <input className={inputClass} value={selectedStep.from || ''} onChange={(e) => set({ from: e.target.value })} placeholder="e.g. Ian / assigned rep" />
            </div>
            <div>
              <label className={labelClass}>Subject</label>
              <input className={inputClass} value={selectedStep.subject || ''} onChange={(e) => set({ subject: e.target.value })} placeholder="Subject line" />
            </div>
            <div>
              <label className={labelClass}>Body</label>
              <textarea className={inputClass} rows={10} value={selectedStep.body || ''} onChange={(e) => set({ body: e.target.value })} placeholder="Email copy…" />
              <TokenChips step={selectedStep} field="body" />
            </div>
          </>
        )}

        {selectedStep.type === 'text' && (
          <>
            <div>
              <label className={labelClass}>From</label>
              <input className={inputClass} value={selectedStep.from || ''} onChange={(e) => set({ from: e.target.value })} placeholder="e.g. Ian / assigned rep" />
            </div>
            <div>
              <label className={labelClass}>Message</label>
              <textarea className={inputClass} rows={6} value={selectedStep.body || ''} onChange={(e) => set({ body: e.target.value })} placeholder="Text message…" />
              <div className="flex items-start justify-between gap-2">
                <TokenChips step={selectedStep} field="body" />
                <span className="text-[11px] text-gray-400 mt-2 whitespace-nowrap">
                  {(selectedStep.body || '').length} chars · {Math.max(1, Math.ceil((selectedStep.body || '').length / 160))} SMS
                </span>
              </div>
            </div>
          </>
        )}

        {selectedStep.type === 'delay' && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>Wait</label>
                <input type="number" min="0" className={inputClass} value={selectedStep.amount} onChange={(e) => set({ amount: e.target.value === '' ? '' : Number(e.target.value) })} />
              </div>
              <div>
                <label className={labelClass}>Unit</label>
                <select className={inputClass} value={selectedStep.unit} onChange={(e) => set({ unit: e.target.value })}>
                  {Object.keys(UNIT_MINUTES).map(unit => <option key={unit} value={unit}>{unit}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Note</label>
              <input className={inputClass} value={selectedStep.note || ''} onChange={(e) => set({ note: e.target.value })} placeholder="e.g. Business days only, send 9am–5pm" />
            </div>
          </>
        )}

        {selectedStep.type === 'call' && (
          <>
            <div>
              <label className={labelClass}>Call purpose</label>
              <input className={inputClass} value={selectedStep.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Intro call to confirm interest" />
            </div>
            <div>
              <label className={labelClass}>Who calls</label>
              <input className={inputClass} value={selectedStep.from || ''} onChange={(e) => set({ from: e.target.value })} placeholder="e.g. Assigned rep" />
            </div>
            <div>
              <label className={labelClass}>Talking points / script</label>
              <textarea className={inputClass} rows={8} value={selectedStep.body || ''} onChange={(e) => set({ body: e.target.value })} placeholder="What to cover on the call…" />
            </div>
          </>
        )}

        {selectedStep.type === 'voicemail' && (
          <div>
            <label className={labelClass}>Voicemail script</label>
            <textarea className={inputClass} rows={6} value={selectedStep.body || ''} onChange={(e) => set({ body: e.target.value })} placeholder="What to say if they don't pick up…" />
            <TokenChips step={selectedStep} field="body" />
          </div>
        )}

        {selectedStep.type === 'task' && (
          <>
            <div>
              <label className={labelClass}>Task</label>
              <input className={inputClass} value={selectedStep.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="e.g. Send handwritten card" />
            </div>
            <div>
              <label className={labelClass}>Assignee</label>
              <input className={inputClass} value={selectedStep.assignee || ''} onChange={(e) => set({ assignee: e.target.value })} placeholder="e.g. Sales rep" />
            </div>
            <div>
              <label className={labelClass}>Notes</label>
              <textarea className={inputClass} rows={4} value={selectedStep.body || ''} onChange={(e) => set({ body: e.target.value })} />
            </div>
          </>
        )}

        {selectedStep.type === 'condition' && (
          <>
            <div>
              <label className={labelClass}>If…</label>
              <input className={inputClass} value={selectedStep.condition || ''} onChange={(e) => set({ condition: e.target.value })} placeholder="e.g. lead replied or booked a call" />
            </div>
            <div>
              <label className={labelClass}>Yes →</label>
              <input className={inputClass} value={selectedStep.ifYes || ''} onChange={(e) => set({ ifYes: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>No →</label>
              <input className={inputClass} value={selectedStep.ifNo || ''} onChange={(e) => set({ ifNo: e.target.value })} />
            </div>
          </>
        )}

        {selectedStep.type === 'stage' && (
          <div>
            <label className={labelClass}>Move lead to</label>
            <select className={inputClass} value={selectedStep.targetStage || ''} onChange={(e) => set({ targetStage: e.target.value })}>
              <option value="">Select a stage…</option>
              {STAGES.filter(s => s.key !== stageKey).map(s => <option key={s.key} value={s.title}>{s.title}</option>)}
            </select>
          </div>
        )}

        {selectedStep.type === 'end' && (
          <div>
            <label className={labelClass}>What happens next</label>
            <textarea className={inputClass} rows={3} value={selectedStep.body || ''} onChange={(e) => set({ body: e.target.value })} placeholder="e.g. Move to long-term follow-up" />
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-gray-800">
          <button type="button" onClick={() => duplicateStep(index)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
            <Copy className="w-4 h-4" />
            Duplicate
          </button>
          <button type="button" onClick={() => deleteStep(index)} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    );
  };

  // ---- page ------------------------------------------------------------

  if (loading) {
    return (
      <div className="min-h-screen p-6 relative">
        <PortalBackground />
        <div className="relative flex justify-center py-24">
          <BBALoader size={40} />
        </div>
      </div>
    );
  }

  const status = STATUS_STYLES[currentVersion?.status] || STATUS_STYLES.draft;

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2 text-center">Drip Campaigns</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-6 text-center">
          How we nurture leads at every stage of the pipeline: messages, texts, calls, and delays
        </p>

        {/* Version bar */}
        <div className={`${cardClass} p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3`}>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <GitFork className="w-4 h-4 text-gray-400 shrink-0" />
            <select
              value={selectedId ?? ''}
              onChange={(e) => handleSwitchVersion(e.target.value)}
              disabled={!selectedId}
              className="min-w-0 max-w-xs px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-semibold bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              {versions.map(v => (
                <option key={v.id ?? 'unsaved'} value={v.id ?? ''}>
                  {v.name}{v.status === 'active' ? ' (Active)' : v.status === 'archived' ? ' (Archived)' : ''}
                </option>
              ))}
            </select>
            <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>{status.label}</span>
            <span className="hidden lg:block text-xs text-gray-400 truncate">
              {currentVersion?.id
                ? `Updated ${formatDate(currentVersion.updatedAt)}${currentVersion.updatedBy ? ` by ${currentVersion.updatedBy}` : ''}${currentVersion.basedOn ? ` · based on ${currentVersion.basedOn}` : ''}`
                : 'Not saved yet'}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {dirty && <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Unsaved changes</span>}
            <button type="button" onClick={openSettings} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200">
              <Settings2 className="w-4 h-4" />
              Version
            </button>
            <button type="button" onClick={openNewVersion} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200">
              <Plus className="w-4 h-4" />
              New version
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 text-sm text-center">{error}</div>
        )}

        {/* Stage tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4">
          {STAGES.map(s => {
            const active = s.key === stageKey;
            const count = stageStepCount(s.key);
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => switchStage(s.key)}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition ${
                  active
                    ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                    : 'bg-white/60 dark:bg-gray-900/60 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
                style={active ? { borderColor: s.color } : undefined}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                {s.title}
                <span className="px-1.5 py-0.5 rounded-md text-[11px] bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Workflow chips + stats */}
        <div className={`${cardClass} p-4 mb-4 flex flex-col lg:flex-row lg:items-center gap-3`}>
          <div className="flex items-center gap-2 flex-wrap flex-1">
            {workflows.map(w => (
              <button
                key={w.id}
                type="button"
                onClick={() => { setWorkflowId(w.id); setSelectedNode('trigger'); setInsertAt(null); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  workflow?.id === w.id
                    ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {w.name || 'Untitled'}
              </button>
            ))}
            <button type="button" onClick={addWorkflow} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium border border-dashed border-gray-300 dark:border-gray-600 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
              <Plus className="w-3.5 h-3.5" />
              Workflow
            </button>
          </div>
          {workflow && (
            <div className="grid grid-cols-5 gap-2 text-center">
              {[
                ['Steps', stats.steps],
                ['Emails', stats.emails],
                ['Texts', stats.texts],
                ['Calls', stats.calls],
                ['Length', formatTotal(stats.duration)]
              ].map(([label, value]) => (
                <div key={label} className="px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-sm font-bold text-gray-900 dark:text-white whitespace-nowrap">{value}</p>
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {!workflow ? (
          <div className={`${cardClass} p-10 text-center`}>
            <p className="text-gray-500 dark:text-gray-400 mb-4">No workflows for {stage.title} yet.</p>
            <button type="button" onClick={addWorkflow} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white">
              <Plus className="w-4 h-4" />
              Create workflow
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-4 items-start">
            {/* Canvas */}
            <div
              className={`${cardClass} p-6 min-h-[520px]`}
              style={{
                backgroundImage: 'radial-gradient(circle, rgba(148,163,184,0.35) 1px, transparent 1px)',
                backgroundSize: '18px 18px'
              }}
            >
              <div className="flex flex-col items-center">
                {/* Trigger */}
                <button
                  type="button"
                  onClick={() => setSelectedNode('trigger')}
                  className={`w-full max-w-md text-left ${cardClass} p-3 border-2 transition hover:shadow-md ${
                    selectedNode === 'trigger' ? 'ring-2 ring-red-500/40' : ''
                  }`}
                  style={{ borderColor: stage.color }}
                >
                  <div className="flex items-center gap-3">
                    <span className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${stage.color}22` }}>
                      <Zap className="w-5 h-5" style={{ color: stage.color }} />
                    </span>
                    <div className="min-w-0">
                      <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: stage.color }}>
                        Trigger · {stage.title}
                      </span>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{workflow.trigger || `Lead enters ${stage.title}`}</p>
                      {workflow.goal && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">Goal: {workflow.goal}</p>}
                    </div>
                  </div>
                </button>

                {steps.map((step, i) => (
                  <React.Fragment key={step.id}>
                    <InsertButton
                      open={insertAt === i}
                      onOpen={() => setInsertAt(i)}
                      onClose={() => setInsertAt(null)}
                      onPick={(type) => insertStep(i, type)}
                    />
                    {renderNode(step, i)}
                  </React.Fragment>
                ))}

                <InsertButton
                  open={insertAt === steps.length}
                  onOpen={() => setInsertAt(steps.length)}
                  onClose={() => setInsertAt(null)}
                  onPick={(type) => insertStep(steps.length, type)}
                />
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  <Flag className="w-3.5 h-3.5" />
                  {steps.length === 0 ? 'Click + to add the first step' : `End · ${formatTotal(stats.duration)} total`}
                </div>
              </div>
            </div>

            {/* Editor */}
            <div className={`${cardClass} p-5 lg:sticky lg:top-6`}>{renderEditor()}</div>
          </div>
        )}
      </div>

      {/* Version modals */}
      {versionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => !saving && setVersionModal(null)}>
          <div className={`${cardClass} w-full max-w-md p-6 space-y-4`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {versionModal === 'new' ? 'New version' : 'Version settings'}
              </h2>
              <button type="button" onClick={() => setVersionModal(null)} className="p-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <label className={labelClass}>Name</label>
              <input className={inputClass} value={modalForm.name || ''} onChange={(e) => setModalForm(f => ({ ...f, name: e.target.value }))} autoFocus />
            </div>
            {versionModal === 'new' ? (
              <div>
                <label className={labelClass}>Start from</label>
                <select className={inputClass} value={modalForm.source} onChange={(e) => setModalForm(f => ({ ...f, source: e.target.value }))}>
                  <option value="current">Copy of {currentVersion?.name}{dirty ? ' (with unsaved edits)' : ''}</option>
                  <option value="blank">Blank</option>
                </select>
                {dirty && modalForm.source === 'current' && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                    Unsaved edits go into the new version only; {currentVersion?.name} keeps its last save.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className={labelClass}>Status</label>
                <select className={inputClass} value={modalForm.status} onChange={(e) => setModalForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active (archives the current active version)</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            )}
            <div>
              <label className={labelClass}>Notes</label>
              <textarea className={inputClass} rows={3} value={modalForm.notes || ''} onChange={(e) => setModalForm(f => ({ ...f, notes: e.target.value }))} placeholder="What changed in this version?" />
            </div>
            <div className="flex items-center justify-between pt-2">
              {versionModal === 'settings' && currentVersion?.id && versions.length > 1 ? (
                <button type="button" onClick={deleteVersion} disabled={saving} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                  Delete version
                </button>
              ) : <span />}
              <div className="flex gap-2">
                <button type="button" onClick={() => setVersionModal(null)} disabled={saving} className="px-4 py-2 rounded-md text-sm font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200">
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={versionModal === 'new' ? createVersion : saveSettings}
                  disabled={saving || !(modalForm.name || '').trim()}
                  className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {saving ? 'Saving…' : versionModal === 'new' ? 'Create' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DripCampaigns;
